import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Divider, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import BannerWidgetComponent from "../../../widgets/BannerWidget/BannerWidgetComponent";
import { defaultSettings } from "../../../widgets/BannerWidget/helper";
import TextPropertyField from "./TextPropertyField";

const useStyles = makeStyles(() => ({
  settingsContainer: {
    border: "1px solid #e1e1e1",
    borderRadius: 5,
    marginTop: "1em",
    padding: 5,
    paddingTop: 10,
    position: "relative",
  },
  textLabel: {
    background: "#fff",
    fontSize: 12,
    color: "#bdb4b4",
    position: "absolute",
    left: 15,
    top: -9,
    minWidth: 80,
    textAlign: "center",
  },
}));

const getSettingsFromLayout = (layout) => {
  const { settings } = layout || {};

  return {
    settings: { ...settings },
  };
};

const BannerWidgetSettings = ({ onDelayedUpdate, selectedWidget }) => {
  const classes = useStyles();
  const intl = useIntl();

  const [isDirty, setIsDirty] = useState(false);
  const [localLayout, setLocalLayout] = useState();
  const [localSettings, setLocalSettings] = useState();

  const previewSettings = useMemo(
    () => (
      {
        headingText: intl.formatMessage({ id: "widget.bannerWidget.propLabel.headingText" }),
        linkText: intl.formatMessage({ id: "widget.bannerWidget.propLabel.linkText" }),
        messageText: intl.formatMessage({ id: "widget.bannerWidget.propLabel.messageText" }),
      }
    ),
    []
  );

  const debouncedSettingsPropertyChange = useCallback(
    debounce((name, value) => {
      setLocalSettings((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          [name]: value || undefined,
        },
      }));
      setIsDirty(true);
    }, 250),
    []
  );

  useEffect(() => {
    if (selectedWidget) {
      if (!localSettings) {
        setLocalLayout(selectedWidget);
      }
      else {
        const { settings } = localSettings;
        setLocalLayout({
          ...selectedWidget,
          settings: {
            ...selectedWidget.settings,
            ...settings
          }
        });
      }
    }
    if (!localSettings) {
      const { settings } = getSettingsFromLayout(selectedWidget);
      setLocalSettings({
        settings: { ...settings },
      });
    }
  }, [selectedWidget, selectedWidget.contentBG, selectedWidget.dividerColor, selectedWidget.name, selectedWidget.titleColor]);

  useEffect(() => {
    if (localSettings) {
      const { settings } = localSettings;
      setLocalLayout((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          ...settings
        }
      }));
    }
  }, [localSettings]);

  useEffect(() => {
    if (localLayout && isDirty) {
      onDelayedUpdate?.(localSettings);
      setIsDirty(false);
    }
  }, [localLayout, isDirty]);

  if (!localSettings) {
    return;
  }

  return (
    <>
      <Box className={classes.settingsContainer}>
        <Typography className={classes.textLabel}>
          <FormattedMessage id="widget.bannerWidget.preview" />
        </Typography>
        <Box sx={{ backgroundColor: selectedWidget?.bgColor || "#ffffff" }}>
          <BannerWidgetComponent
            layout={{
              ...localLayout,
              settings: {
                ...localLayout.settings,
                ...previewSettings
              }
            }}
            sx={{ position: "relative" }}
          />
          <Divider />
          <BannerWidgetComponent
            layout={localLayout}
            sx={{ position: "relative" }}
          />
        </Box>
      </Box>
      <Box sx={{
        padding: 0,
        paddingBottom: 0,
        paddingTop: 1.5,
        "& > .MuiTextField-root": {
          marginBottom: 1.5,
          "&:last-of-type": {
            marginBottom: 0,
          },
        }
      }}>
        <TextPropertyField
          initialValue={localSettings.settings.headingText}
          label={<FormattedMessage id="widget.bannerWidget.propLabel.headingText" />}
          onChange={(value) => debouncedSettingsPropertyChange("headingText", value)}
          placeholder={defaultSettings.headingText}
        />
        <TextPropertyField
          initialValue={localSettings.settings.messageText}
          label={<FormattedMessage id="widget.bannerWidget.propLabel.messageText" />}
          multiline
          onChange={(value) => debouncedSettingsPropertyChange("messageText", value)}
          placeholder={defaultSettings.messageText}
        />
        <TextPropertyField
          initialValue={localSettings.settings.linkText}
          label={<FormattedMessage id="widget.bannerWidget.propLabel.linkText" />}
          onChange={(value) => debouncedSettingsPropertyChange("linkText", value)}
          placeholder={defaultSettings.linkText}
        />
        <TextPropertyField
          initialValue={localSettings.settings.linkUrl}
          label={<FormattedMessage id="widget.bannerWidget.propLabel.linkUrl" />}
          onChange={(value) => debouncedSettingsPropertyChange("linkUrl", value)}
          placeholder="https://www.uniqueue.se"
        />
      </Box>
    </>
  );
};

export default BannerWidgetSettings;
